import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  ChevronLeft, Lock, Building2, ArrowRight, ArrowRightLeft, FileText, Download, CheckCircle2, Info, Search
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';

const TYPE_TOOLTIPS = {
  RFI: 'Request for Information — You are being asked to provide information about your company, capabilities, and experience. No pricing is required at this stage. Answer the questionnaire honestly and thoroughly — strong responses may lead to you being shortlisted for a formal RFQ or RFP.',
  RFQ: 'Request for Quotation — You are being asked to submit your best pricing for specific items or services. The requirements are clearly defined, so review them carefully and provide competitive unit prices, delivery times, and payment terms. Price is the primary evaluation criterion.',
  RFP: 'Request for Proposal — You are being asked to submit a detailed proposal explaining how you would deliver the required goods or services. This includes your technical approach, relevant experience, team qualifications, and pricing. Your response will be scored against multiple criteria, so take time to address each requirement thoroughly.',
  REVERSE_AUCTION: 'Reverse Auction — You are invited to compete in a live, real-time bidding event. During the auction window, you submit bids to lower your price against other suppliers. You may see the current lowest bid or your rank depending on the auction rules. If a bid is placed near the closing time, the clock may extend automatically to give everyone a fair chance to respond. Have your best price ready before the auction starts.',
};

function useCountdown(endDateStr) {
  const [timeLeft, setTimeLeft] = useState('');
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!endDateStr) { setTimeLeft(''); return; }

    const parseDate = (s) => {
      // Handle "12-Jun-2026 12:00 PM" format
      const months = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
      const m = s.match(/(\d{1,2})-([A-Za-z]{3})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (m) {
        let hour = parseInt(m[4]);
        if (m[6]?.toUpperCase() === 'PM' && hour < 12) hour += 12;
        if (m[6]?.toUpperCase() === 'AM' && hour === 12) hour = 0;
        return new Date(parseInt(m[3]), months[m[2]], parseInt(m[1]), hour, parseInt(m[5]));
      }
      return new Date(s);
    };

    const calc = () => {
      const end = parseDate(endDateStr);
      const now = new Date();
      const diff = end - now;
      if (diff <= 0) { setTimeLeft('Closed'); return; }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(`${days}d ${hours}h ${mins}m ${secs}s`);
    };

    calc();
    intervalRef.current = setInterval(calc, 1000);
    return () => clearInterval(intervalRef.current);
  }, [endDateStr]);

  return timeLeft;
}

export default function PublicTendersPage() {
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTender, setSelectedTender] = useState(null);
  const [branding, setBranding] = useState({ company_name: '', logo_url: null });
  const [closedSort, handleClosedSort] = useSortState('closing_date', 'desc');

  // Check if loaded inside supplier portal iframe with auth token
  const params = new URLSearchParams(window.location.search);
  const supplierToken = params.get('supplier_token');
  const isEmbedded = !!supplierToken;

  const [respondedRefs, setRespondedRefs] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    api.get('/api/system/public/branding').then(r => setBranding(r.data)).catch(() => {});
    api.get('/api/system/public/rfx-events').then(r => setTenders(r.data.data || [])).catch(() => {}).finally(() => setLoading(false));
    // If embedded with token, load supplier's responded events
    if (supplierToken) {
      api.get('/api/supplier-portal/my-rfx', { headers: { Authorization: `Bearer ${supplierToken}` } })
        .then(r => {
          const refs = new Set((r.data.data || []).filter(e => e.relationship === 'RESPONDED' || e.relationship === 'BID_SUBMITTED').map(e => e.event_ref));
          setRespondedRefs(refs);
        }).catch(() => {});
    }
  }, []); // eslint-disable-line

  const logoSrc = branding.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}` : null;
  const openTenders = tenders.filter(t => t.status === 'OPEN');
  const closedTenders = tenders.filter(t => ['CLOSED', 'EVALUATION', 'AWARDED'].includes(t.status));

  const Footer = () => (
    <div className="text-center py-6 mt-8">
      <p className="text-[10px] text-slate-400">&copy; {new Date().getFullYear()} Eyvo eProcurement. All rights reserved.</p>
      <a href="https://www.eyvo.com" target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-500 hover:text-blue-600">
        Powered by Eyvo eProcurement
      </a>
    </div>
  );

  // ── Tender Detail View ──
  if (selectedTender) {
    return <TenderDetail tender={selectedTender} branding={branding} logoSrc={logoSrc} onBack={() => setSelectedTender(null)} Footer={Footer} supplierToken={supplierToken} isEmbedded={isEmbedded} />;
  }

  // ── Main Tenders Listing ──
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {logoSrc && <img src={logoSrc} alt="" className="h-10 object-contain" />}
          <h1 className="text-lg font-bold">{branding.company_name || 'Procurement'} — Sourcing Events</h1>
        </div>
        {!isEmbedded && (
          <Button size="sm" className="bg-white/20 text-white border border-white/40 hover:bg-white/30 font-semibold"
            onClick={() => window.location.href = '/supplier-portal'}>
            <Building2 className="h-3.5 w-3.5 mr-1" /> Supplier Portal Login
          </Button>
        )}
      </div>

      <div className="max-w-[1400px] mx-auto p-6">

        {/* Instructions Panel — fixed glossary + configurable text from System Admin */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-3">Instructions</h2>
          <div className="border rounded-xl p-5 space-y-3" style={{ background: '#FFF8E1', borderColor: '#FFE0B2' }}>
            {/* Fixed glossary line with tooltips — not editable */}
            <p className="text-sm">
              This portal lists all open sourcing events from our organisation. Events may be published as a{' '}
              <span className="relative group/rfp inline font-semibold text-indigo-700 cursor-help underline decoration-dotted">RFP
                <span className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover/rfp:opacity-100 group-hover/rfp:visible transition-colors pointer-events-none leading-relaxed">Request for Proposal — You will be asked to submit a detailed proposal covering your approach, experience, team, and pricing. Your response is scored on multiple criteria, so address each requirement carefully.</span>
              </span>, {' '}
              <span className="relative group/rfq inline font-semibold text-indigo-700 cursor-help underline decoration-dotted">RFQ
                <span className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover/rfq:opacity-100 group-hover/rfq:visible transition-colors pointer-events-none leading-relaxed">Request for Quotation — You will be asked to provide your best pricing for clearly defined items or services. Review the specifications and submit competitive prices, delivery times, and terms.</span>
              </span>, {' '}
              <span className="relative group/rfi inline font-semibold text-indigo-700 cursor-help underline decoration-dotted">RFI
                <span className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover/rfi:opacity-100 group-hover/rfi:visible transition-colors pointer-events-none leading-relaxed">Request for Information — You will be asked to provide information about your company and capabilities. No pricing is needed. Strong responses may lead to being shortlisted for a formal sourcing event.</span>
              </span>, or as a {' '}
              <span className="relative group/ra inline font-semibold text-indigo-700 cursor-help underline decoration-dotted">Reverse Auction
                <span className="absolute left-0 top-full mt-1 z-50 w-80 p-3 bg-slate-900 text-white text-[11px] rounded-lg shadow-xl opacity-0 invisible group-hover/ra:opacity-100 group-hover/ra:visible transition-colors pointer-events-none leading-relaxed">Reverse Auction — You will compete in a live bidding event, lowering your price in real time against other suppliers. The clock may extend if bids arrive near closing. Have your best price ready before the auction starts.</span>
              </span>.
            </p>

            {/* Editable instructions from System Admin → RFx Sourcing → Section 3 */}
            {branding.rfx_portal_text && (
              <div className="text-sm space-y-2 [&_a]:text-indigo-700 [&_a]:underline [&_a]:font-semibold [&_strong]:font-semibold [&_strong]:text-indigo-700"
                dangerouslySetInnerHTML={{ __html: branding.rfx_portal_text }} />
            )}

            {/* Contact email — auto-appended from Section 4 */}
            {branding.rfx_enquiry_email ? (
              <p className="text-sm mt-1">If you experience any difficulties, please contact us at <a href={`mailto:${branding.rfx_enquiry_email}`} className="text-indigo-700 font-semibold underline hover:text-indigo-900">{branding.rfx_enquiry_email}</a>.</p>
            ) : (
              <p className="text-sm mt-1 text-slate-500">If you experience any difficulties, please contact the issuing organisation directly.</p>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md mb-4">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by reference, title, category..."
            className="w-full pl-10 pr-4 h-10 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-sm">&times;</button>}
        </div>

        {/* Filter: move expired events from open to closed client-side */}
        {(() => {
          const now = Date.now();
          const sq = searchQuery.toLowerCase();
          const matchesSearch = (t) => !sq || t.event_ref?.toLowerCase().includes(sq) || t.title?.toLowerCase().includes(sq) || (t.category || '').toLowerCase().includes(sq);
          const actuallyOpen = openTenders.filter(t => (!t.closing_date || new Date(t.closing_date).getTime() > now) && matchesSearch(t));
          const expiredButOpen = openTenders.filter(t => t.closing_date && new Date(t.closing_date).getTime() <= now);
          const allClosed = [...closedTenders, ...expiredButOpen].filter(matchesSearch);
          return (
            <>
              {/* Currently Open - by type */}
              {['RFP', 'RFQ', 'RFI', 'REVERSE_AUCTION'].map(type => {
                const items = actuallyOpen.filter(t => t.type === type);
                if (items.length === 0) return null;
                const typeLabel = type === 'REVERSE_AUCTION' ? 'Reverse Auctions' : `${type}s`;
                return <OpenTendersTable key={type} tenders={items} type={type} typeLabel={typeLabel} onSelect={setSelectedTender} respondedRefs={respondedRefs} isEmbedded={isEmbedded} />;
              })}

              {/* Closed — all types combined, sorted by closing date */}
              {allClosed.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-lg font-bold text-slate-500">Closed RFx</h2>
                    <span className="bg-slate-200 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full">{allClosed.length}</span>
                  </div>
                  <div className="border rounded-xl overflow-hidden bg-white" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    <table className="w-full text-sm whitespace-nowrap">
                      <thead className="sticky top-0 z-10"><tr className="bg-muted/50 border-b">
                        <SortableTableHeader label="Reference" field="event_ref" sortState={closedSort} onSort={handleClosedSort} />
                        <SortableTableHeader label="Type" field="type" sortState={closedSort} onSort={handleClosedSort} />
                        <SortableTableHeader label="Description" field="title" sortState={closedSort} onSort={handleClosedSort} className="min-w-[200px]" />
                        <SortableTableHeader label="Category" field="category" sortState={closedSort} onSort={handleClosedSort} />
                        <SortableTableHeader label="Issued" field="created_at" sortState={closedSort} onSort={handleClosedSort} />
                        <SortableTableHeader label="Last Modified" field="last_modified" sortState={closedSort} onSort={handleClosedSort} />
                        <SortableTableHeader label="Expired" field="closing_date" sortState={closedSort} onSort={handleClosedSort} />
                      </tr></thead>
                      <tbody>
                        {sortData(allClosed, closedSort.field, closedSort.dir)
                          .map(t => {
                          const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : '—';
                          return (
                          <tr key={t.event_ref} className="border-b hover:bg-slate-50/50">
                            <td className="px-3 py-2.5 font-mono text-slate-500">{t.event_ref}</td>
                            <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${t.type === 'REVERSE_AUCTION' ? 'bg-rose-100 text-rose-700' : t.type === 'RFI' ? 'bg-blue-100 text-blue-700' : t.type === 'RFQ' ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'}`}>{t.type === 'REVERSE_AUCTION' ? 'Auction' : t.type}</span></td>
                            <td className="px-3 py-2.5 text-slate-600 whitespace-normal max-w-[300px]">{t.title}</td>
                            <td className="px-3 py-2.5 text-slate-500">{t.category || '—'}</td>
                            <td className="px-3 py-2.5 text-slate-400">{fmtD(t.start_date || t.created_at)}</td>
                            <td className="px-3 py-2.5 text-slate-400">{fmtD(t.updated_at)}</td>
                            <td className="px-3 py-2.5 text-slate-400">{fmtD(t.closing_date)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          );
        })()}

        {loading && <div className="text-center py-12 text-muted-foreground">Loading sourcing events...</div>}

        <Footer />
      </div>
    </div>
  );
}


function getUrgencyColor(closingDate) {
  if (!closingDate) return { bg: '#e2e8f0', pct: 0, label: 'No closing date' };
  const now = Date.now();
  const close = new Date(closingDate).getTime();
  const remaining = close - now;
  if (remaining <= 0) return { bg: '#ef4444', pct: 100, label: 'Expired' };
  const days = Math.ceil(remaining / (24 * 3600 * 1000));
  const totalWindow = 30 * 24 * 3600 * 1000;
  const pct = Math.min(100, Math.max(0, ((totalWindow - remaining) / totalWindow) * 100));
  const label = days <= 1 ? 'Closing today' : days <= 3 ? `Closing in ${days} days — urgent` : days <= 7 ? `Closing in ${days} days` : `Closing in ${days} days`;
  if (pct < 40) return { bg: `hsl(${120 - pct * 1.5}, 70%, 45%)`, pct, label };
  if (pct < 70) return { bg: `hsl(${60 - (pct - 40) * 2}, 80%, 45%)`, pct, label };
  return { bg: `hsl(${0 + Math.max(0, 20 - (pct - 70) * 0.7)}, 85%, 45%)`, pct, label };
}

function OpenTendersTable({ tenders, type, typeLabel, onSelect, respondedRefs, isEmbedded }) {
  const [sortKey, setSortKey] = useState('closing_date');
  const [sortDir, setSortDir] = useState('asc');

  if (tenders.length === 0) return null;

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...tenders].sort((a, b) => {
    let va = a[sortKey] || '';
    let vb = b[sortKey] || '';
    if (sortKey === 'closing_date' || sortKey === 'start_date' || sortKey === 'created_at') {
      va = va ? new Date(va).getTime() : 0;
      vb = vb ? new Date(vb).getTime() : 0;
    } else {
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
    }
    return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
  });

  const SortHeader = ({ label, field, className = '' }) => (
    <th className={`text-left px-3 py-2.5 font-semibold cursor-pointer select-none hover:bg-muted/80 transition-colors ${className}`}
      onClick={() => handleSort(field)}>
      <span className="flex items-center gap-1">
        {label}
        {sortKey === field && <span className="text-[10px]">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
      </span>
    </th>
  );

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-bold">Currently Open {typeLabel}</h2>
          <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">{tenders.length}</span>
          <div className="relative group">
            <Info className="w-4 h-4 text-slate-400 cursor-help" />
            <div className="absolute left-0 top-full mt-1 z-50 w-96 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-colors duration-200 leading-relaxed pointer-events-none">
              {TYPE_TOOLTIPS[type]}
            </div>
          </div>
        </div>
      </div>
      <div className="border rounded-xl overflow-x-auto bg-white">
        <table className="w-full text-sm whitespace-nowrap">
          <thead><tr className="bg-muted/50 border-b">
            <SortHeader label="Reference No" field="event_ref" />
            <SortHeader label="Description" field="title" className="min-w-[200px]" />
            <SortHeader label="Category" field="category" />
            <SortHeader label="Issued" field="start_date" />
            <SortHeader label="Last Modified" field="updated_at" />
            <SortHeader label="Closing Date and Time" field="closing_date" className="min-w-[220px]" />
          </tr></thead>
          <tbody>
            {sorted.map(t => {
              const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : '—';
              const closingDate = t.closing_date ? new Date(t.closing_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
              const hasResponded = respondedRefs?.has(t.event_ref);
              const urgency = getUrgencyColor(t.closing_date);
              return (
                <tr key={t.event_ref} className={`border-b cursor-pointer transition-colors ${hasResponded ? 'bg-emerald-50 hover:bg-emerald-100' : 'hover:bg-blue-50/50'}`}
                  onClick={() => onSelect(t)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(t); } }}
                  tabIndex={0} role="row" aria-label={`${t.event_ref} — ${t.title}`}>
                  <td className="px-3 py-3 font-mono font-semibold text-indigo-700">{t.event_ref}</td>
                  <td className="px-3 py-3 whitespace-normal max-w-[300px]">{t.title}</td>
                  <td className="px-3 py-3 text-slate-600">{t.category || '—'}</td>
                  <td className="px-3 py-3">{fmtDate(t.start_date || t.created_at)}</td>
                  <td className="px-3 py-3 text-slate-500">{fmtDate(t.updated_at)}</td>
                  <td className="px-3 py-0" aria-label={`${closingDate} — ${urgency.label}`}>
                    <div className="relative rounded-md overflow-hidden" style={{ backgroundColor: urgency.bg + '20' }}>
                      <div className="absolute inset-0 rounded-md" style={{ backgroundColor: urgency.bg, opacity: 0.15 }} />
                      <div className="absolute left-0 top-0 bottom-0 rounded-md transition-colors" style={{ width: `${urgency.pct}%`, backgroundColor: urgency.bg, opacity: 0.25 }} />
                      <span className="relative z-10 block px-3 py-3 font-semibold text-sm" style={{ color: urgency.bg }}>
                        {closingDate}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function ClosedTendersTable({ tenders, type, typeLabel }) {
  if (tenders.length === 0) return null;
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-lg font-bold">Closed {typeLabel}</h2>
        <span className="bg-slate-200 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full">{tenders.length}</span>
        <div className="relative group">
          <Info className="w-4 h-4 text-slate-400 cursor-help" />
          <div className="absolute left-0 top-full mt-1 z-50 w-96 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-colors duration-200 leading-relaxed pointer-events-none">
            {TYPE_TOOLTIPS[type]}
          </div>
        </div>
      </div>
      <div className="border rounded-xl overflow-hidden bg-white">
        <div className="max-h-[300px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0"><tr className="bg-muted/50 border-b">
              <th className="text-left px-4 py-2.5 font-semibold italic">Reference No</th>
              <th className="text-left px-4 py-2.5 font-semibold italic">Description</th>
              <th className="text-left px-4 py-2.5 font-semibold italic">Closed Date</th>
            </tr></thead>
            <tbody>
              {tenders.map(t => {
                const closingDate = t.closing_date ? new Date(t.closing_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : '—';
                return (
                  <tr key={t.event_ref} className="border-b">
                    <td className="px-4 py-2.5 text-muted-foreground font-mono">{t.event_ref}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{t.title}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{closingDate}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function TenderDetail({ tender: t, branding, logoSrc, onBack, Footer, supplierToken, isEmbedded }) {
  const countdown = useCountdown(t.closing_date);
  const isAuction = t.type === 'REVERSE_AUCTION';
  const typeLabel = isAuction ? 'Reverse Auction' : t.type;
  const startDate = t.start_date ? new Date(t.start_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
  const closingDate = t.closing_date ? new Date(t.closing_date).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const [eventItems, setEventItems] = useState([]);
  const [eventLots, setEventLots] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/system/public/rfx-events/${t.event_ref}/items`)
      .then(r => {
        setEventItems(r.data.items || []);
        setEventLots(r.data.lots || []);
      })
      .catch(() => {})
      .finally(() => setItemsLoading(false));
  }, [t.event_ref]);

  const hasLots = eventLots.length > 0 || eventItems.some(i => i.lot);
  const hasSpecs = eventItems.some(i => i.specifications);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white px-6 py-3 flex items-center gap-3">
        {logoSrc && <img src={logoSrc} alt="" className="h-8 object-contain" />}
        <h1 className="text-sm font-bold">{branding.company_name} — Sourcing Events</h1>
      </div>
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2 cursor-pointer" onClick={onBack}>
            <ChevronLeft className="h-5 w-5 text-primary" /> {typeLabel}
          </h2>
        </div>

        {/* Header info box */}
        <div className="border rounded-xl bg-white mb-6">
          <div className="grid grid-cols-2 divide-x">
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div><p className="text-[10px] text-muted-foreground">Start Date</p><p className="font-bold whitespace-nowrap">{startDate}</p></div>
                <div><p className="text-[10px] text-muted-foreground">Closing Date</p><p className="font-bold whitespace-nowrap">{closingDate}</p></div>
                <div><p className="text-[10px] text-muted-foreground">Reference Number</p><p className="font-bold">{t.event_ref}</p></div>
              </div>
              {t.description && (
                <div>
                  <p className="text-xs font-bold">Description</p>
                  <p className="text-sm">{t.description}</p>
                </div>
              )}
            </div>
            <div className="p-5 space-y-2 text-sm">
              <div className="flex"><span className="text-muted-foreground w-36 shrink-0">Type</span><span>: <strong>{typeLabel}</strong></span></div>
              <div className="flex"><span className="text-muted-foreground w-36 shrink-0">Status</span><span>: <strong>{t.status}</strong></span></div>
              {isAuction && <div className="flex"><span className="text-muted-foreground w-36 shrink-0">Bidding Structure</span><span>: {(t.bidding_structure || '').replace(/_/g, ' ')}</span></div>}
              {t.sealed_bidding?.enabled && <div className="flex text-amber-700"><Lock className="w-3.5 h-3.5 mr-1 mt-0.5" /> Sealed bidding — responses hidden until reveal date</div>}
              <div className="flex"><span className="text-muted-foreground w-36 shrink-0">Closing in</span><span>: <span className="text-red-500 font-bold">{countdown || '—'}</span></span></div>
            </div>
          </div>
        </div>

        {/* Line Items / Auction Items */}
        <div className="border rounded-xl bg-white mb-6 overflow-hidden" data-testid="public-event-items">
          <div className="px-5 py-3 border-b bg-slate-50 flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-600" />
            <h3 className="font-bold text-sm">Items Requiring Your Bid / Response</h3>
            <span className="ml-auto text-xs text-slate-500">{eventItems.length} item(s)</span>
          </div>
          {itemsLoading ? (
            <div className="p-8 text-center text-sm text-slate-400">Loading items...</div>
          ) : eventItems.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No items listed for this event.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-muted/30 border-b">
                  <th className="px-4 py-2.5 text-left font-semibold w-12">#</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Description</th>
                  <th className="px-4 py-2.5 text-right font-semibold w-24">Quantity</th>
                  <th className="px-4 py-2.5 text-left font-semibold w-20">UOM</th>
                  {hasLots && <th className="px-4 py-2.5 text-left font-semibold w-36">Lot</th>}
                  {hasSpecs && <th className="px-4 py-2.5 text-left font-semibold min-w-[200px]">Specifications</th>}
                </tr></thead>
                <tbody>
                  {eventItems.map((item, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50/50" data-testid={`public-item-row-${idx}`}>
                      <td className="px-4 py-2.5 font-mono text-slate-400">{item.line_number}</td>
                      <td className="px-4 py-2.5">{item.description}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{Number(item.quantity || 0).toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-slate-600">{item.uom || '—'}</td>
                      {hasLots && <td className="px-4 py-2.5 text-slate-600">{item.lot || '—'}</td>}
                      {hasSpecs && <td className="px-4 py-2.5 text-slate-500 whitespace-normal">{item.specifications || '—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Public Documents */}
        {t.public_documents?.length > 0 && (
          <div className="border rounded-xl bg-white mb-6 p-5">
            <h3 className="font-bold text-sm mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-600" /> Available Documents</h3>
            <div className="space-y-2">
              {t.public_documents.map((d, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:bg-indigo-50 transition-colors">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium">{d.file_name}</span>
                  </div>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/rfx/public/events/${d.event_ref}/documents/${d.file_name}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 px-3 py-1.5 rounded bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 transition-colors">
                    <Download className="w-3.5 h-3.5" /> Download
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action area — respond if authenticated, login prompt if not */}
        {isEmbedded && supplierToken ? (
          <div className="mt-6 p-4 border-2 border-primary/30 rounded-xl text-center bg-primary/5">
            <CheckCircle2 className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-sm font-semibold mb-1">You are logged in and can respond to this {typeLabel}.</p>
            <p className="text-xs text-muted-foreground mb-3">Click below to submit your response or bid.</p>
            <Button size="sm" onClick={() => {
              // Navigate parent frame to the event detail in the supplier portal
              if (window.parent !== window) {
                window.parent.postMessage({ type: 'OPEN_EVENT', event_ref: t.event_ref }, '*');
              } else {
                window.location.href = `/supplier-portal`;
              }
            }}>
              <ArrowRight className="h-3.5 w-3.5 mr-1" /> Respond to this {typeLabel}
            </Button>
          </div>
        ) : (
          <div className="mt-6 p-4 border-2 border-dashed border-primary/30 rounded-xl text-center bg-primary/5">
            <Lock className="h-6 w-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-semibold mb-1">Want to respond to this {typeLabel}?</p>
            <p className="text-xs text-muted-foreground mb-3">You must be a registered and approved supplier to submit a bid or response.</p>
            <div className="flex gap-2 justify-center">
              <Button size="sm" onClick={() => window.location.href = '/supplier-portal'}>
                <ArrowRight className="h-3.5 w-3.5 mr-1" /> Log In to Respond
              </Button>
              <Button size="sm" variant="outline" onClick={() => window.location.href = '/supplier-portal'}>
                Register as Supplier
              </Button>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
}
